import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
} from "@mui/x-data-grid";
import * as XLSX from "xlsx";

import { API_URL } from "../../components/api";
import "./Account.css";
import statementLogo from "../Collection/logo.png";


import RequestHistory from "../../Requests/requestsHistory";

const REFRESH_INTERVAL = 30000;

const SECTIONS = [
  { key: "overview", label: "Overview" },
  { key: "resellers", label: "Resellers" },
  { key: "transactions", label: "Transactions" },
  {
    key: "transfer-requests",
    label: "Transfer Requests",
  },
  { key: "invoices", label: "Invoices" },
  { key: "plan-upgrades", label: "Plan Upgrades" },
  { key: "testing-requests", label: "Testing Requests" },
  {
    key: "receipt-breakdown",
    label: "Receipt Breakdown",
  },
  { key: "receipts", label: "Receipts" },
  { key: "payments", label: "System Payments" },
  { key: "inventory", label: "Inventory" },
  { key: "site-orders", label: "Site Orders" },
  { key: "logs", label: "Accounting Logs" },
  { key: "account-holders", label: "Account Holders" },
  { key: "accounts", label: "Chart of Accounts" },
  { key: "balances", label: "Balances" },
  { key: "journal", label: "Journal Logs" },
  { key: "shareholders", label: "Shareholders" },
];

const INVENTORY_SECTIONS = [
  { key: "inventory", label: "Stock" },
  { key: "inventory-products", label: "Products" },
  { key: "inventory-services", label: "Services" },
  { key: "inventory-categories", label: "Categories" },
  { key: "inventory-suppliers", label: "Suppliers" },
  { key: "inventory-warehouses", label: "Warehouses" },
  { key: "inventory-transfers", label: "Transfer Requests" },
  { key: "inventory-movements", label: "Stock Movements" },
  {
    key: "inventory-purchase-requests",
    label: "Purchase Requests",
  },
  {
    key: "inventory-purchase-orders",
    label: "Purchase Orders",
  },
  {
    key: "inventory-goods-receipts",
    label: "Goods Receipts",
  },
  { key: "inventory-refunds", label: "Refunds" },
  { key: "inventory-audit", label: "Audit" },
  { key: "inventory-evaluations", label: "Evaluations" },
];

const DATE_FILTER_SECTIONS = new Set([
  "transactions",
  "transfer-requests",
  "invoices",
  "plan-upgrades",
  "testing-requests",
  "receipt-breakdown",
  "receipts",
  "payments",
  "site-orders",
  "journal",
]);

const STATEMENT_SECTIONS = new Set([
  "transactions",
  "invoices",
  "receipt-breakdown",
  "receipts",
]);

const ROW_ID_FIELDS = {
  resellers: "dealer_id",
  transactions: "dealer_transaction_id",
  "transfer-requests": "request_id",
  invoices: "invoice_id",
  "plan-upgrades": "invoice_id",
  "testing-requests": "request_id",
  "receipt-breakdown": "invoice_receipt_id",
  receipts: "receipt_id",
  payments: "payment_id",
  inventory: "inventory_id",
  "site-orders": "order_id",
  "inventory-products": "product_id",
  "inventory-services": "service_id",
  "inventory-categories": "category_id",
  "inventory-suppliers": "supplier_id",
  "inventory-warehouses": "warehouse_id",
  "inventory-transfers": "transaction_id",
  "inventory-movements": "inventory_audit_event_id",
  "inventory-purchase-requests": "purchase_request_id",
  "inventory-purchase-orders": "purchase_order_id",
  "inventory-goods-receipts": "goods_receipt_id",
  "inventory-refunds": "purchase_refund_id",
  "inventory-audit": "inventory_audit_event_id",
  "inventory-evaluations": "inventory_evaluation_id",
  logs: "action_id",
  "account-holders": "account_holder_id",
  accounts: "account_id",
  balances: "account_holder_balance_id",
  journal: "journal_id",
  shareholders: "shareholder_id",
};

const inventoryTextColumn = (
  field,
  headerName,
  minWidth = 140,
  flex = 1,
) => ({
  field,
  headerName,
  minWidth,
  flex,
});

const inventoryAmountColumn = (
  field,
  headerName,
  minWidth = 130,
) => ({
  field,
  headerName,
  minWidth,
  flex: 0.75,
  valueFormatter: ({ value }) => formatAmount(value),
});

const inventoryDateColumn = (
  field,
  headerName,
  minWidth = 170,
) => ({
  field,
  headerName,
  minWidth,
  flex: 0.9,
  valueFormatter: ({ value }) => formatDate(value),
});

const INVENTORY_COLUMN_SETS = {
  "inventory-products": [
    inventoryTextColumn("product_id", "Product ID", 105, 0.5),
    inventoryTextColumn("name", "Product", 180, 1.2),
    inventoryTextColumn("category_name", "Category", 140),
    inventoryTextColumn("supplier_name", "Supplier", 170),
    inventoryTextColumn("code", "Code", 110, 0.7),
    inventoryTextColumn("barcode", "Barcode", 140),
    inventoryAmountColumn("cost", "Cost"),
    inventoryAmountColumn("selling_price", "Price"),
    inventoryAmountColumn("retail_price", "Retail Price"),
    inventoryTextColumn("description", "Description", 220, 1.5),
  ],
  "inventory-services": [
    inventoryTextColumn("service_id", "Service ID", 105, 0.5),
    inventoryTextColumn("code", "Code", 120, 0.7),
    inventoryTextColumn("name", "Service", 190, 1.2),
    inventoryTextColumn("description", "Description", 260, 1.6),
    inventoryAmountColumn("cost", "Cost"),
    inventoryAmountColumn("selling_price", "Price"),
  ],
  "inventory-categories": [
    inventoryTextColumn("category_id", "Category ID", 115, 0.5),
    inventoryTextColumn("category_name", "Category", 220, 1.2),
    inventoryTextColumn("type", "Type", 170),
  ],
  "inventory-suppliers": [
    inventoryTextColumn("supplier_id", "Supplier ID", 110, 0.5),
    inventoryTextColumn("company_name", "Company", 200, 1.2),
    inventoryTextColumn("full_name", "Full Name", 190, 1.1),
    inventoryTextColumn("phone", "Phone", 150),
    inventoryAmountColumn("balance_usd", "Balance USD", 145),
  ],
  "inventory-warehouses": [
    inventoryTextColumn("warehouse_id", "Warehouse ID", 120, 0.5),
    inventoryTextColumn("warehouse_name", "Warehouse", 220, 1.2),
    inventoryTextColumn("manager_id", "Manager ID", 110, 0.5),
    inventoryTextColumn("manager_name", "Manager", 190, 1.1),
    inventoryTextColumn(
      "account_holder_id",
      "Account Holder ID",
      145,
      0.6,
    ),
  ],
  "inventory-transfers": [
    inventoryTextColumn("transaction_id", "Transfer ID", 105, 0.5),
    inventoryTextColumn(
      "request_reference",
      "Reference",
      250,
      1.2,
    ),
    inventoryTextColumn("product_name", "Product", 180, 1.1),
    inventoryTextColumn("quantity", "Quantity", 105, 0.5),
    inventoryTextColumn(
      "from_warehouse_name",
      "Source Warehouse",
      175,
    ),
    inventoryTextColumn(
      "to_warehouse_name",
      "Destination Warehouse",
      195,
    ),
    inventoryTextColumn("initiator", "Initiator", 150),
    inventoryTextColumn("status", "Status", 125, 0.7),
    inventoryTextColumn("reason", "Reason", 230, 1.4),
    inventoryTextColumn("reviewer", "Reviewer", 150),
    inventoryTextColumn(
      "decision_reason",
      "Decision Reason",
      230,
      1.4,
    ),
    inventoryDateColumn(
      "request_created_at",
      "Requested",
    ),
    inventoryDateColumn("decision_date", "Decision Date"),
  ],
  "inventory-movements": [
    inventoryTextColumn(
      "inventory_audit_event_id",
      "Record ID",
      105,
      0.5,
    ),
    inventoryTextColumn("event_type", "Event", 210, 1.1),
    inventoryTextColumn("product_name", "Product", 180),
    inventoryTextColumn(
      "source_warehouse_name",
      "Source Warehouse",
      175,
    ),
    inventoryTextColumn(
      "destination_warehouse_name",
      "Destination Warehouse",
      195,
    ),
    inventoryTextColumn(
      "warehouse_name",
      "Warehouse",
      170,
    ),
    inventoryTextColumn(
      "quantity_delta",
      "Quantity Change",
      135,
      0.6,
    ),
    inventoryTextColumn(
      "quantity_before",
      "Before",
      100,
      0.5,
    ),
    inventoryTextColumn(
      "quantity_after",
      "After",
      100,
      0.5,
    ),
    inventoryTextColumn("actor", "Actor", 160),
    inventoryTextColumn(
      "description",
      "Description",
      280,
      1.7,
    ),
    inventoryDateColumn("creation_date", "Date"),
  ],
  "inventory-purchase-requests": [
    inventoryTextColumn(
      "purchase_request_id",
      "Request ID",
      110,
      0.5,
    ),
    inventoryTextColumn(
      "initiator_name",
      "Requester",
      180,
    ),
    inventoryTextColumn(
      "receiver_name",
      "Receiver",
      180,
    ),
    inventoryTextColumn("status", "Status", 130, 0.7),
    inventoryDateColumn("creation_date", "Created"),
    inventoryDateColumn("updated_at", "Updated"),
    inventoryDateColumn("accepted_at", "Accepted"),
  ],
  "inventory-purchase-orders": [
    inventoryTextColumn(
      "purchase_order_id",
      "Order ID",
      105,
      0.5,
    ),
    inventoryTextColumn(
      "purchase_request_id",
      "Request ID",
      110,
      0.5,
    ),
    inventoryTextColumn("supplier_name", "Supplier", 190),
    inventoryTextColumn(
      "created_by_name",
      "Created By",
      170,
    ),
    inventoryAmountColumn(
      "total_amount",
      "Total USD",
      140,
    ),
    inventoryTextColumn("status", "Status", 130, 0.7),
    inventoryDateColumn(
      "expected_delivery_date",
      "Expected Delivery",
    ),
    inventoryDateColumn("creation_date", "Created"),
    inventoryDateColumn(
      "balance_applied_at",
      "Balance Applied",
    ),
  ],
  "inventory-goods-receipts": [
    inventoryTextColumn(
      "goods_receipt_id",
      "Receipt ID",
      105,
      0.5,
    ),
    inventoryTextColumn(
      "purchase_order_id",
      "Order ID",
      105,
      0.5,
    ),
    inventoryTextColumn("supplier_name", "Supplier", 190),
    inventoryTextColumn(
      "warehouse_name",
      "Warehouse",
      180,
    ),
    inventoryTextColumn(
      "received_by_name",
      "Received By",
      170,
    ),
    inventoryTextColumn("reference", "Reference", 170),
    inventoryTextColumn("notes", "Notes", 260, 1.5),
    inventoryDateColumn("creation_date", "Received"),
  ],
  "inventory-refunds": [
    inventoryTextColumn(
      "purchase_refund_id",
      "Refund ID",
      105,
      0.5,
    ),
    inventoryTextColumn(
      "goods_receipt_id",
      "Receipt ID",
      105,
      0.5,
    ),
    inventoryTextColumn(
      "purchase_order_id",
      "Order ID",
      105,
      0.5,
    ),
    inventoryTextColumn("supplier_name", "Supplier", 180),
    inventoryTextColumn("product_name", "Product", 180),
    inventoryTextColumn("quantity", "Quantity", 100, 0.5),
    inventoryAmountColumn(
      "unit_price_usd",
      "Unit Price USD",
      145,
    ),
    inventoryAmountColumn(
      "amount_usd",
      "Refund USD",
      135,
    ),
    inventoryAmountColumn(
      "balance_before_usd",
      "Balance Before",
      150,
    ),
    inventoryAmountColumn(
      "balance_after_usd",
      "Balance After",
      150,
    ),
    inventoryTextColumn(
      "processed_by_name",
      "Processed By",
      170,
    ),
    inventoryTextColumn("reason", "Reason", 240, 1.4),
    inventoryDateColumn("creation_date", "Date"),
  ],
  "inventory-audit": [
    inventoryTextColumn(
      "inventory_audit_event_id",
      "Event ID",
      105,
      0.5,
    ),
    inventoryTextColumn("event_type", "Event", 220, 1.1),
    inventoryTextColumn("actor", "Actor", 160),
    inventoryTextColumn(
      "purchase_request_id",
      "Request ID",
      110,
      0.5,
    ),
    inventoryTextColumn(
      "purchase_order_id",
      "Order ID",
      105,
      0.5,
    ),
    inventoryTextColumn(
      "goods_receipt_id",
      "Receipt ID",
      105,
      0.5,
    ),
    inventoryTextColumn("supplier_name", "Supplier", 180),
    inventoryTextColumn("warehouse_name", "Warehouse", 170),
    inventoryTextColumn("product_name", "Product", 180),
    inventoryTextColumn(
      "quantity_delta",
      "Quantity Change",
      135,
      0.6,
    ),
    inventoryAmountColumn("amount_usd", "Amount USD"),
    inventoryTextColumn(
      "previous_status",
      "Previous Status",
      150,
    ),
    inventoryTextColumn("new_status", "New Status", 145),
    inventoryTextColumn(
      "description",
      "Description",
      300,
      1.8,
    ),
    inventoryDateColumn("creation_date", "Date"),
  ],
  "inventory-evaluations": [
    inventoryTextColumn(
      "inventory_evaluation_id",
      "Evaluation ID",
      120,
      0.5,
    ),
    inventoryTextColumn(
      "warehouse_name",
      "Warehouse",
      190,
    ),
    inventoryTextColumn(
      "stock_operator_name",
      "Stock Operator",
      190,
    ),
    inventoryTextColumn("status", "Status", 130, 0.7),
    inventoryTextColumn(
      "product_count",
      "Products",
      105,
      0.5,
    ),
    inventoryTextColumn(
      "matched_products",
      "Matched",
      105,
      0.5,
    ),
    inventoryTextColumn(
      "discrepancy_products",
      "Discrepancies",
      125,
      0.6,
    ),
    inventoryDateColumn("creation_date", "Date"),
  ],
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const number = Number(value);

  if (Number.isNaN(number)) {
    return value;
  }

  return number.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
};

const formatMonth = (value) => {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
};

const textColumn = (
  field,
  headerName,
  minWidth = 150,
  flex = 1,
) => ({
  field,
  headerName,
  minWidth,
  flex,
});

const amountColumn = (
  field,
  headerName,
  minWidth = 135,
) => ({
  field,
  headerName,
  minWidth,
  flex: 1,
  valueFormatter: ({ value }) => formatAmount(value),
});

const dateColumn = (
  field,
  headerName = "Date",
) => ({
  field,
  headerName,
  minWidth: 175,
  flex: 1,
  valueFormatter: ({ value }) => formatDate(value),
});

const COLUMN_SETS = {
  inventory: [
    textColumn(
      "inventory_id",
      "Inventory ID",
      110,
      0,
    ),
    textColumn(
      "product_id",
      "Product ID",
      100,
      0,
    ),
    textColumn(
      "product_name",
      "Product",
      190,
    ),
    textColumn(
      "warehouse_id",
      "Warehouse ID",
      115,
      0,
    ),
    textColumn(
      "warehouse_name",
      "Warehouse",
      170,
    ),
    textColumn(
      "quantity",
      "Quantity",
      100,
      0,
    ),
    textColumn(
      "account_holder_id",
      "Account Holder ID",
      145,
      0,
    ),
    textColumn(
      "account_holder_name",
      "Account Holder",
      170,
    ),
  ],
  resellers: [
    textColumn("display_name", "Reseller", 190),
    textColumn("company_name", "Company", 170),
    textColumn("status", "Status", 110),
    amountColumn("balance_usd", "Balance USD"),
    amountColumn("balance_lbp", "Balance LBP"),
    textColumn("position", "Position", 150),
    amountColumn("credits", "Credits"),
    amountColumn("commission", "Commission"),
    amountColumn("receipt_paid_usd", "Received USD"),
    textColumn("account_holder_name", "Account Holder", 170),
  ],
  transactions: [
    textColumn("dealer_transaction_id", "ID", 80, 0),
    textColumn("dealer_name", "Reseller", 180),
    textColumn("action", "Action", 120),
    textColumn("debit_account", "Debit Account", 160),
    textColumn("credit_account", "Credit Account", 160),
    amountColumn("amount", "Amount"),
    amountColumn("balance_record", "Balance Record"),
    textColumn("details", "Details", 260, 2),
    dateColumn("creation_date"),
  ],
  "transfer-requests": [
    {
      field: "resolved_at",
      headerName: "Resolved At",
      minWidth: 175,
      flex: 1,
      valueFormatter: ({ value }) => (
        formatDate(value)
      ),
    },
    textColumn(
      "request_id",
      "Request ID",
      100,
      0,
    ),
    textColumn(
      "outcome",
      "Outcome",
      105,
    ),
    textColumn(
      "request_status",
      "Request Status",
      120,
    ),
    textColumn(
      "response",
      "Response",
      105,
    ),
    textColumn(
      "transaction_status",
      "Transaction Status",
      140,
    ),
    textColumn(
      "consistency",
      "Record Check",
      140,
    ),
    textColumn(
      "type",
      "Request Type",
      165,
    ),
    textColumn(
      "source_drawer_name",
      "Source Drawer",
      160,
    ),
    textColumn(
      "destination_drawer_name",
      "Destination Drawer",
      170,
    ),
    amountColumn(
      "amount_usd",
      "Amount USD",
    ),
    amountColumn(
      "amount_lbp",
      "Amount LBP",
    ),
    textColumn(
      "linked_receipt_count",
      "Receipts",
      90,
    ),
    textColumn(
      "linked_payment_count",
      "Payments",
      90,
    ),
    textColumn(
      "initiator",
      "Initiator",
      130,
    ),
    textColumn(
      "receiver",
      "Receiver",
      130,
    ),
    textColumn(
      "description",
      "Description",
      260,
      2,
    ),
    dateColumn("date"),
  ],
  "testing-requests": [
    textColumn("request_id", "Request ID", 100, 0),
    textColumn("client_name", "Client", 190),
    textColumn(
      "subscription_username",
      "Subscription",
      150,
    ),
    textColumn(
      "original_plan_name",
      "Original Plan",
      135,
    ),
    textColumn(
      "tested_plan_name",
      "Tested Plan",
      135,
    ),
    textColumn("requested_by", "Requested By", 130),
    dateColumn("checked_at", "Checked At"),
    dateColumn("expires_at", "Testing Expiry"),
    textColumn("status", "Status", 105),
    textColumn("action", "Action", 100),
    textColumn("acted_by", "Action By", 125),
    dateColumn("acted_at", "Action At"),
    textColumn("invoice_id", "Invoice ID", 100),
    amountColumn("net_due", "Net Due USD"),
  ],
  "plan-upgrades": [
    textColumn(
      "invoice_id",
      "Invoice ID",
      100,
      0,
    ),
    dateColumn("date"),
    textColumn(
      "client_name",
      "Client",
      190,
    ),
    textColumn(
      "subscription_username",
      "Subscription",
      160,
    ),
    textColumn(
      "old_plan_name",
      "Old Plan",
      135,
    ),
    textColumn(
      "new_plan_name",
      "New Plan",
      135,
    ),
    textColumn(
      "remaining_days",
      "Days Remaining",
      125,
    ),
    amountColumn(
      "unused_credit",
      "Unused Credit (USD)",
    ),
    amountColumn(
      "remaining_charge",
      "New Plan Charge (USD)",
    ),
    amountColumn(
      "net_due",
      "Net Due (USD)",
    ),
    amountColumn(
      "wallet_before",
      "Balance Before (USD)",
    ),
    amountColumn(
      "wallet_after",
      "Balance After (USD)",
    ),
    textColumn(
      "expiry_date",
      "Expiry Unchanged",
      140,
    ),
    textColumn(
      "status",
      "Invoice Status",
      120,
    ),
    textColumn(
      "initiator",
      "Initiator",
      130,
    ),
  ],
  invoices: [
    textColumn("invoice_id", "Invoice ID", 100, 0),
    textColumn("party_type", "Party Type", 130),
    textColumn("party_name", "Party", 190),
    textColumn("dealer_name", "Reseller", 180),
    textColumn("description", "Description", 240, 2),
    amountColumn("total_amount", "Gross"),
    amountColumn("discount", "Discount"),
    amountColumn("net_amount", "Net"),
    amountColumn(
      "paid_toward_invoice_usd",
      "Paid (USD)",
    ),
    amountColumn(
      "remaining_amount_usd",
      "Remaining (USD)",
    ),
    textColumn(
      "active_receipt_count",
      "Active Receipts",
      120,
    ),
    textColumn(
      "payment_breakdown_status",
      "Payment Breakdown",
      150,
    ),
    textColumn("status", "Status", 110),
    textColumn("type", "Type", 110),
    textColumn("initiator", "Initiator", 130),
    amountColumn("balance_record", "Balance Record"),
    textColumn("account_holder_id", "Holder ID", 100),
    dateColumn("date"),
  ],
  "site-orders": [
    textColumn("order_id", "Order ID", 95, 0),
    textColumn(
      "order_number",
      "Order Number",
      140,
    ),
    textColumn("site_name", "Site", 180),
    textColumn(
      "warehouse_name",
      "Warehouse",
      180,
    ),
    textColumn(
      "account_holder_name",
      "Account Holder",
      190,
    ),
    amountColumn(
      "reference_total_usd",
      "Reference Total (USD)",
    ),
    textColumn("status", "Status", 115),
    textColumn("initiator", "Initiator", 130),
    dateColumn("date"),
  ],
  "receipt-breakdown": [
    textColumn(
      "invoice_receipt_id",
      "Breakdown ID",
      115,
      0,
    ),
    textColumn(
      "receipt_id",
      "Receipt ID",
      100,
      0,
    ),
    textColumn(
      "invoice_id",
      "Invoice ID",
      100,
      0,
    ),
    textColumn(
      "party_type",
      "Party Type",
      120,
    ),
    textColumn(
      "party_name",
      "Party",
      190,
    ),
    textColumn(
      "dealer_name",
      "Reseller",
      180,
    ),
    amountColumn(
      "paid_toward_invoice_usd",
      "Paid Toward Invoice (USD)",
    ),
    amountColumn(
      "invoice_net_amount",
      "Invoice Net (USD)",
    ),
    amountColumn(
      "invoice_active_paid_usd",
      "Invoice Paid (USD)",
    ),
    amountColumn(
      "invoice_remaining_usd",
      "Remaining (USD)",
    ),
    textColumn(
      "breakdown_status",
      "Breakdown Status",
      130,
    ),
    textColumn(
      "receipt_status",
      "Receipt Status",
      120,
    ),
    textColumn(
      "invoice_status",
      "Invoice Status",
      120,
    ),
    textColumn(
      "initiator",
      "Initiator",
      130,
    ),
    textColumn(
      "description",
      "Receipt Description",
      240,
      2,
    ),
    textColumn(
      "invoice_description",
      "Invoice Description",
      240,
      2,
    ),
    dateColumn("created_at"),
  ],
  receipts: [
    textColumn("receipt_id", "Receipt ID", 100, 0),
    textColumn("party_type", "Party Type", 130),
    textColumn("party_name", "Party", 190),
    textColumn("dealer_name", "Reseller", 180),
    textColumn("description", "Description", 240, 2),
    amountColumn("paid_amount_usd", "Paid USD"),
    amountColumn("paid_amount_lbp", "Paid LBP"),
    amountColumn("returned_usd", "Returned USD"),
    amountColumn("returned_lbp", "Returned LBP"),
    amountColumn("net_received_usd", "Net USD"),
    amountColumn("net_received_lbp", "Net LBP"),
    amountColumn(
      "paid_toward_invoices_usd",
      "Paid Toward Invoices (USD)",
    ),
    textColumn(
      "invoice_count",
      "Invoices",
      90,
    ),
    textColumn(
      "payment_breakdown_status",
      "Payment Breakdown",
      150,
    ),
    amountColumn("exchange_rate", "Exchange Rate"),
    textColumn("status", "Status", 120),
    textColumn("initiator", "Initiator", 130),
    amountColumn("balance_record", "Balance Record"),
    dateColumn("date"),
  ],
  payments: [
    textColumn("payment_id", "Payment ID", 105, 0),
    textColumn("supplier_name", "Supplier", 180),
    textColumn("category_name", "Category", 150),
    textColumn("description", "Description", 260, 2),
    amountColumn("paid_amount_usd", "Paid USD"),
    amountColumn("paid_amount_lbp", "Paid LBP"),
    amountColumn("returned_lbp", "Returned LBP"),
    textColumn("status", "Status", 115),
    textColumn("completed", "Completed", 105),
    textColumn("initiator", "Initiator", 130),
    textColumn("account_holder_id", "Holder ID", 100),
    dateColumn("date"),
  ],
  logs: [
    textColumn("action_id", "Log ID", 90, 0),
    textColumn("action_type", "Action", 145),
    textColumn("user", "User", 130),
    textColumn("action_reason", "Reason", 180),
    textColumn("action_description", "Description", 320, 3),
    dateColumn("creation_date"),
  ],
  "account-holders": [
    textColumn("account_holder_id", "Holder ID", 100, 0),
    textColumn("display_name", "Display Name", 190),
    textColumn("name_en", "English Name", 180),
    textColumn("name_ar", "Arabic Name", 180),
    textColumn("holder_type", "Type", 120),
    textColumn("contact_info_id", "Contact ID", 105),
    textColumn("address_id", "Address ID", 105),
    dateColumn("creation_date", "Created"),
  ],
  accounts: [
    textColumn("account_id", "Account ID", 100, 0),
    textColumn("account_holder_name", "Account Holder", 180),
    textColumn("account_nb", "Account Number", 150),
    textColumn("account_name_en", "English Name", 180),
    textColumn("account_name_ar", "Arabic Name", 180),
    textColumn("account_type", "Type", 120),
    textColumn("currency", "Currency", 100),
    textColumn("description_en", "English Description", 230, 2),
    textColumn("description_ar", "Arabic Description", 230, 2),
    dateColumn("created_at", "Created"),
    dateColumn("updated_at", "Updated"),
  ],
  balances: [
    textColumn("account_holder_balance_id", "Link ID", 90, 0),
    textColumn("account_holder_name", "Account Holder", 190),
    textColumn("balance_id", "Balance ID", 100),
    textColumn("balance_type", "Balance Type", 130),
    amountColumn("balance_usd", "Balance USD"),
    amountColumn("balance_lbp", "Balance LBP"),
    textColumn("balance_notes", "Notes", 240, 2),
    dateColumn("creation_date", "Created"),
  ],
  journal: [
    textColumn("journal_id", "Journal ID", 100, 0),
    textColumn("account_nb", "Account Number", 145),
    textColumn("account_name_en", "English Account", 180),
    textColumn("account_name_ar", "Arabic Account", 180),
    textColumn("account_holder_name", "Account Holder", 180),
    textColumn("order_item_id", "Order Item", 105),
    amountColumn("amount", "Amount"),
    dateColumn("creation_date"),
  ],
  shareholders: [
    textColumn("shareholder_id", "Shareholder ID", 120, 0),
    textColumn("account_holder_name", "Account Holder", 180),
    textColumn("name_en", "English Name", 180),
    textColumn("name_ar", "Arabic Name", 180),
    amountColumn("percentage", "Percentage"),
    textColumn("contact_info_id", "Contact ID", 105),
    textColumn("address_id", "Address ID", 105),
  ],
};

const CURRENT_POSITION_CARD_KEYS = new Set([
  "resellers_owe_us_usd",
  "we_owe_resellers_usd",
  "available_reseller_credit_usd",
  "overdrawn_reseller_credit_usd",
]);

const NON_CANCELLED_RESELLER_CARD_KEYS = new Set([
  "gross_reseller_invoices_usd",
  "reseller_invoice_discounts_usd",
  "net_reseller_invoices_usd",
  "reseller_receipts_usd",
  "reseller_receipts_lbp",
  "receipts_in_transfer",
]);

const PERIOD_DETAIL_FIELDS = {
  invoices: [
    "invoice_id",
    "party_type",
    "party_name",
    "total_amount",
    "discount",
    "net_amount",
    "status",
    "date",
  ],
  receipts: [
    "receipt_id",
    "party_type",
    "party_name",
    "paid_amount_usd",
    "paid_amount_lbp",
    "discount_usd",
    "discount_lbp",
    "returned_usd",
    "returned_lbp",
    "net_received_usd",
    "net_received_lbp",
    "status",
    "date",
  ],
  payments: [
    "payment_id",
    "supplier_name",
    "category_name",
    "paid_amount_usd",
    "paid_amount_lbp",
    "returned_lbp",
    "status",
    "date",
  ],
};

const INVOICE_STATUS_COLUMNS = [
  textColumn("label", "Status", 140),
  textColumn("count", "Invoices", 100),
  amountColumn("gross", "Invoice Amount"),
  amountColumn("discount", "Discount"),
  amountColumn("net", "After Discount"),
];

const RECEIPT_STATUS_COLUMNS = [
  textColumn("label", "Status", 140),
  textColumn("count", "Receipts", 100),
  amountColumn("paid_usd", "Paid USD"),
  amountColumn("discount_usd", "Discount USD"),
  amountColumn("returned_usd", "Returned USD"),
  amountColumn("net_usd", "Net USD"),
  amountColumn("paid_lbp", "Paid LBP"),
  amountColumn("discount_lbp", "Discount LBP"),
  amountColumn("returned_lbp", "Returned LBP"),
  amountColumn("net_lbp", "Net LBP"),
];

const PAYMENT_STATUS_COLUMNS = [
  textColumn("label", "Status", 140),
  textColumn("count", "Payments", 100),
  amountColumn("paid_usd", "Paid USD"),
  amountColumn("paid_lbp", "Paid LBP"),
  amountColumn("returned_lbp", "Returned LBP"),
  amountColumn("net_lbp", "Net LBP"),
];

const toInputDate = (value) => {
  const year = value.getFullYear();
  const month = String(
    value.getMonth() + 1
  ).padStart(2, "0");
  const day = String(
    value.getDate()
  ).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const CARD_LABELS = {
  resellers_owe_us_usd: "Resellers Owe Us (USD)",
  we_owe_resellers_usd: "We Owe Resellers (USD)",
  available_reseller_credit_usd: "Available Reseller Credit",
  overdrawn_reseller_credit_usd: "Overdrawn Reseller Credit",
  gross_reseller_invoices_usd: "Non-Cancelled Reseller Invoice Amount",
  reseller_invoice_discounts_usd: "Non-Cancelled Invoice Discounts",
  net_reseller_invoices_usd: "Non-Cancelled Invoice After Discount",
  reseller_receipts_usd: "Non-Cancelled Reseller Receipts (USD)",
  reseller_receipts_lbp: "Non-Cancelled Reseller Receipts (LBP)",
  receipts_in_transfer: "Receipts In Transfer",
};

const COUNT_LABELS = {
  resellers: "Resellers",
  resellers_with_account_holder: "Linked Resellers",
  resellers_without_account_holder: "Unlinked Resellers",
  reseller_transactions: "Reseller Transactions",
  reseller_invoices: "Reseller Invoices",
  reseller_receipts: "Reseller Receipts",
  system_payments: "System Payments",
  account_holders: "Account Holders",
  accounts: "Accounts",
  balance_links: "Balance Links",
  journal_logs: "Journal Logs",
  shareholders: "Shareholders",
};


const DETAIL_CONFIG = {
  cards: {
    resellers_owe_us_usd: {
      section: "resellers",
      fields: ["display_name", "balance_usd"],
      params: { metric: "resellers_owe_us_usd" },
    },
    we_owe_resellers_usd: {
      section: "resellers",
      fields: ["display_name", "balance_usd"],
      params: { metric: "we_owe_resellers_usd" },
    },
    available_reseller_credit_usd: {
      section: "resellers",
      fields: ["display_name", "credits"],
      params: { metric: "available_reseller_credit_usd" },
    },
    overdrawn_reseller_credit_usd: {
      section: "resellers",
      fields: ["display_name", "credits"],
      params: { metric: "overdrawn_reseller_credit_usd" },
    },
    gross_reseller_invoices_usd: {
      section: "invoices",
      fields: [
        "invoice_id",
        "dealer_name",
        "total_amount",
        "date",
      ],
      params: {},
    },
    reseller_invoice_discounts_usd: {
      section: "invoices",
      fields: [
        "invoice_id",
        "dealer_name",
        "discount",
        "date",
      ],
      params: {},
    },
    net_reseller_invoices_usd: {
      section: "invoices",
      fields: [
        "invoice_id",
        "dealer_name",
        "total_amount",
        "discount",
        "net_amount",
        "date",
      ],
      params: {},
    },
    reseller_receipts_usd: {
      section: "receipts",
      fields: [
        "receipt_id",
        "dealer_name",
        "paid_amount_usd",
        "returned_usd",
        "net_received_usd",
        "date",
      ],
      params: {},
    },
    reseller_receipts_lbp: {
      section: "receipts",
      fields: [
        "receipt_id",
        "dealer_name",
        "paid_amount_lbp",
        "returned_lbp",
        "net_received_lbp",
        "date",
      ],
      params: {},
    },
    receipts_in_transfer: {
      section: "receipts",
      fields: [
        "receipt_id",
        "dealer_name",
        "status",
        "paid_amount_usd",
        "paid_amount_lbp",
        "date",
      ],
      params: { status: "in_transfer" },
    },
  },
  counts: {
    resellers: {
      section: "resellers",
      fields: [
        "display_name",
        "company_name",
        "status",
      ],
      params: {},
    },
    resellers_with_account_holder: {
      section: "resellers",
      fields: [
        "display_name",
        "account_holder_name",
      ],
      params: {
        metric: "resellers_with_account_holder",
      },
    },
    resellers_without_account_holder: {
      section: "resellers",
      fields: [
        "display_name",
        "account_holder_name",
      ],
      params: {
        metric: "resellers_without_account_holder",
      },
    },
    reseller_transactions: {
      section: "transactions",
      fields: [
        "dealer_transaction_id",
        "dealer_name",
        "action",
        "amount",
        "creation_date",
      ],
      params: {},
    },
    reseller_invoices: {
      section: "invoices",
      fields: [
        "invoice_id",
        "dealer_name",
        "total_amount",
        "discount",
        "net_amount",
        "date",
      ],
      params: {},
    },
    reseller_receipts: {
      section: "receipts",
      fields: [
        "receipt_id",
        "dealer_name",
        "net_received_usd",
        "net_received_lbp",
        "date",
      ],
      params: {},
    },
    system_payments: {
      section: "payments",
      fields: [
        "payment_id",
        "supplier_name",
        "category_name",
        "paid_amount_usd",
        "paid_amount_lbp",
        "date",
      ],
      params: {},
    },
    account_holders: {
      section: "account-holders",
      fields: [
        "account_holder_id",
        "display_name",
        "holder_type",
        "creation_date",
      ],
      params: {},
    },
    accounts: {
      section: "accounts",
      fields: [
        "account_id",
        "account_holder_name",
        "account_nb",
        "account_name_en",
        "account_type",
        "currency",
      ],
      params: {},
    },
    balance_links: {
      section: "balances",
      fields: [
        "account_holder_balance_id",
        "account_holder_name",
        "balance_type",
        "balance_usd",
        "balance_lbp",
      ],
      params: {},
    },
    journal_logs: {
      section: "journal",
      fields: [
        "journal_id",
        "account_nb",
        "account_holder_name",
        "amount",
        "creation_date",
      ],
      params: {},
    },
    shareholders: {
      section: "shareholders",
      fields: [
        "shareholder_id",
        "account_holder_name",
        "name_en",
        "name_ar",
        "percentage",
      ],
      params: {},
    },
  },
};

const DetailToolbar = () => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarFilterButton />
    <GridToolbarExport />
  </GridToolbarContainer>
);


const MONTHLY_INVOICE_COLUMNS = [
  {
    field: "month",
    headerName: "Month",
    minWidth: 180,
    flex: 1,
    valueFormatter: ({ value }) => formatMonth(value),
  },
  textColumn("count", "Invoices", 110, 0),
  amountColumn("gross", "Gross USD"),
  amountColumn("discount", "Discount USD"),
  amountColumn("net", "Net USD"),
];

const MONTHLY_RECEIPT_COLUMNS = [
  {
    field: "month",
    headerName: "Month",
    minWidth: 180,
    flex: 1,
    valueFormatter: ({ value }) => formatMonth(value),
  },
  textColumn("count", "Receipts", 110, 0),
  amountColumn("paid_usd", "Paid USD"),
  amountColumn("paid_lbp", "Paid LBP"),
  amountColumn("returned_usd", "Returned USD"),
  amountColumn("returned_lbp", "Returned LBP"),
];

const MonthlyToolbar = () => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarFilterButton />
    <GridToolbarExport />
  </GridToolbarContainer>
);

const ACCOUNT_REPORT_SECTIONS = new Set([
  "transfer-requests",
  "invoices",
  "receipt-breakdown",
  "receipts",
  "payments",
]);

const ACCOUNT_SECTION_SUMMARY_LABELS = {
  invoices: {
    record_count: "Invoices · All Statuses",
    gross_amount_usd: "Gross USD · All Statuses",
    discount_amount_usd: "Discount USD · All Statuses",
    after_discount_amount_usd: "After Discount USD · All Statuses",
    average_after_discount_usd: "Average USD · All Statuses",
  },
  receipts: {
    record_count: "Receipts · All Statuses",
    paid_amount_usd: "Paid USD · All Statuses",
    paid_amount_lbp: "Paid LBP · All Statuses",
    discount_amount_usd: "Discount USD · All Statuses",
    discount_amount_lbp: "Discount LBP · All Statuses",
    returned_amount_usd: "Returned USD · All Statuses",
    returned_amount_lbp: "Returned LBP · All Statuses",
    net_recorded_usd: "Net Recorded USD · All Statuses",
    net_recorded_lbp: "Net Recorded LBP · All Statuses",
  },
  payments: {
    record_count: "Payments · All Statuses",
    paid_amount_usd: "Paid USD · All Statuses",
    paid_amount_lbp: "Paid LBP · All Statuses",
    returned_amount_lbp: "Returned LBP · All Statuses",
    net_recorded_lbp: "Net Recorded LBP · All Statuses",
  },
};

const ACCOUNT_TODAY_CARDS = [
  {
    key: "invoice_count",
    label: "Invoices Today",
    section: "invoices",
  },
  {
    key: "non_cancelled_invoice_after_discount_usd",
    label: "Non-Cancelled Invoice Value USD",
    section: "invoices",
    statusCategory: "non_cancelled",
  },
  {
    key: "receipt_count",
    label: "Receipts Today",
    section: "receipts",
  },
  {
    key: "net_receipts_recorded_usd",
    label: "Net Receipts Recorded USD",
    section: "receipts",
    statusCategory: "non_cancelled",
  },
  {
    key: "net_receipts_recorded_lbp",
    label: "Net Receipts Recorded LBP",
    section: "receipts",
    statusCategory: "non_cancelled",
  },
  {
    key: "receipts_in_transfer",
    label: "Receipts In Transfer",
    section: "receipts",
    statusCategory: "in_transfer",
  },
  {
    key: "payment_count",
    label: "System Payments Today",
    section: "payments",
  },
  {
    key: "payments_recorded_usd",
    label: "System Payments Recorded USD",
    section: "payments",
  },
  {
    key: "net_payments_recorded_lbp",
    label: "Net System Payments Recorded LBP",
    section: "payments",
  },
  {
    key: "payments_in_transfer",
    label: "System Payments In Transfer",
    section: "payments",
    statusCategory: "in_transfer",
  },
];

const MONTHLY_PAYMENT_COLUMNS = [
  {
    field: "month",
    headerName: "Month",
    minWidth: 170,
    flex: 1,
    valueFormatter: (params) => formatMonth(params.value),
  },
  {
    field: "count",
    headerName: "Payments",
    minWidth: 110,
    flex: 0.7,
  },
  {
    field: "paid_usd",
    headerName: "Paid USD",
    minWidth: 130,
    flex: 1,
    valueFormatter: (params) => formatAmount(params.value),
  },
  {
    field: "paid_lbp",
    headerName: "Paid LBP",
    minWidth: 130,
    flex: 1,
    valueFormatter: (params) => formatAmount(params.value),
  },
  {
    field: "returned_lbp",
    headerName: "Returned LBP",
    minWidth: 140,
    flex: 1,
    valueFormatter: (params) => formatAmount(params.value),
  },
  {
    field: "net_lbp",
    headerName: "Net Recorded LBP",
    minWidth: 150,
    flex: 1,
    valueFormatter: (params) => formatAmount(params.value),
  },
];

const ACCOUNT_STATEMENT_PAGE_SIZE = 100;

const ACCOUNT_STATEMENT_INACTIVE_STATUSES = new Set([
  "cancelled",
  "rejected",
  "refused",
]);

const statementNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const formatStatementMoney = (value) => (
  statementNumber(value).toLocaleString(
    "en-US",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  )
);

const formatStatementDate = (value) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString(
    "en-GB",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    },
  );
};

const formatStatementDateOnly = (value) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleDateString(
    "en-GB",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    },
  );
};

const statementDealerName = (dealer) => (
  dealer?.display_name
  || dealer?.company_name
  || dealer?.full_name
  || `Reseller ${dealer?.dealer_id || ""}`
);

const statementEscapeHtml = (value) => (
  String(value ?? "")
    .replace(
      /[&<>"']/g,
      (character) => ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;",
      })[character],
    )
);

const isActiveStatementRecord = (status) => (
  !ACCOUNT_STATEMENT_INACTIVE_STATUSES.has(
    String(status || "").trim().toLowerCase(),
  )
);

const buildResellerStatementRows = (
  invoices,
  receipts,
) => {
  const invoiceRows = invoices
    .filter((invoice) => (
      isActiveStatementRecord(invoice.status)
    ))
    .map((invoice) => {
      const netAmount = (
        invoice.net_amount == null
          ? (
            statementNumber(invoice.total_amount)
            - statementNumber(invoice.discount)
          )
          : statementNumber(invoice.net_amount)
      );

      return {
        key: `invoice-${invoice.invoice_id}`,
        recordOrder: 0,
        recordId: statementNumber(invoice.invoice_id),
        type: "Invoice",
        reference: `Invoice #${invoice.invoice_id}`,
        date: invoice.date,
        description: invoice.description || "-",
        status: invoice.status || "-",
        debitUsd: Math.max(netAmount, 0),
        creditUsd: 0,
        creditLbp: 0,
      };
    });

  const receiptRows = receipts
    .filter((receipt) => (
      isActiveStatementRecord(receipt.status)
    ))
    .map((receipt) => {
      const creditUsd = (
        receipt.net_received_usd == null
          ? (
            statementNumber(receipt.paid_amount_usd)
            - statementNumber(receipt.returned_usd)
          )
          : statementNumber(receipt.net_received_usd)
      );

      const creditLbp = (
        receipt.net_received_lbp == null
          ? (
            statementNumber(receipt.paid_amount_lbp)
            - statementNumber(receipt.returned_lbp)
          )
          : statementNumber(receipt.net_received_lbp)
      );

      return {
        key: `receipt-${receipt.receipt_id}`,
        recordOrder: 1,
        recordId: statementNumber(receipt.receipt_id),
        type: "Receipt",
        reference: `Receipt #${receipt.receipt_id}`,
        date: receipt.date,
        description: receipt.description || "-",
        status: receipt.status || "-",
        debitUsd: 0,
        creditUsd: Math.max(creditUsd, 0),
        creditLbp: Math.max(creditLbp, 0),
      };
    });

  return [...invoiceRows, ...receiptRows].sort(
    (left, right) => {
      const leftTime = new Date(left.date).getTime();
      const rightTime = new Date(right.date).getTime();

      const safeLeftTime = Number.isFinite(leftTime)
        ? leftTime
        : 0;
      const safeRightTime = Number.isFinite(rightTime)
        ? rightTime
        : 0;

      if (safeLeftTime !== safeRightTime) {
        return safeLeftTime - safeRightTime;
      }

      if (left.recordOrder !== right.recordOrder) {
        return left.recordOrder - right.recordOrder;
      }

      return left.recordId - right.recordId;
    },
  );
};

const getResellerStatementTotals = (rows) => (
  rows.reduce(
    (totals, row) => ({
      invoicedUsd: totals.invoicedUsd + row.debitUsd,
      receivedUsd: totals.receivedUsd + row.creditUsd,
      receivedLbp: totals.receivedLbp + row.creditLbp,
    }),
    {
      invoicedUsd: 0,
      receivedUsd: 0,
      receivedLbp: 0,
    },
  )
);

const getResellerStatementPeriod = (rows) => {
  const dates = rows
    .map((row) => new Date(row.date).getTime())
    .filter(Number.isFinite)
    .sort((left, right) => left - right);

  if (dates.length === 0) {
    return "No activity";
  }

  return (
    `${formatStatementDateOnly(dates[0])}`
    + " - "
    + `${formatStatementDateOnly(dates[dates.length - 1])}`
  );
};

function Account() {
  const [
    drawerRequestId,
    setDrawerRequestId,
  ] = useState(null);

  const history = useHistory();

  const authTokens = JSON.parse(
    localStorage.getItem("authTokens") || "null",
  );
  const accessToken = authTokens?.access;

  const [authorized, setAuthorized] = useState(null);
  const [activeSection, setActiveSection] = useState("overview");
  const [
    activeInventorySection,
    setActiveInventorySection,
  ] = useState("inventory");
  const [overview, setOverview] = useState(null);
  const [rows, setRows] = useState([]);
  const [sectionSummary, setSectionSummary] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 25,
    total_pages: 1,
    total_rows: 0,
  });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [draftDateFrom, setDraftDateFrom] = useState("");
  const [draftDateTo, setDraftDateTo] = useState("");
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [statementRows, setStatementRows] = useState([]);
  const [statementLoading, setStatementLoading] =
    useState(false);
  const [statementError, setStatementError] =
    useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailRows, setDetailRows] = useState([]);
  const [detailPagination, setDetailPagination] = useState({
    page: 1,
    page_size: 25,
    total_pages: 1,
    total_rows: 0,
  });
  const [detailPage, setDetailPage] = useState(0);
  const [detailPageSize, setDetailPageSize] = useState(25);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  const statementTotals = getResellerStatementTotals(
    statementRows,
  );
  const statementPeriod = getResellerStatementPeriod(
    statementRows,
  );

  const catalogSection = (
    activeSection === "inventory"
      ? activeInventorySection
      : activeSection
  );

  const currentSectionLabel = (
    activeSection === "inventory"
      ? (
        INVENTORY_SECTIONS.find(
          (section) => section.key === catalogSection,
        )?.label || "Inventory"
      )
      : (
        SECTIONS.find(
          (section) => section.key === activeSection,
        )?.label || ""
      )
  );

  const latestRequest = useRef(0);
  const latestDetailRequest = useRef(0);

  const detailColumns = useMemo(() => {
    if (!detail) {
      return [];
    }

    const availableColumns = (
      COLUMN_SETS[detail.section] || []
    );

    return detail.fields
      .map((field) => (
        availableColumns.find(
          (column) => column.field === field,
        )
      ))
      .filter(Boolean);
  }, [detail]);

  useEffect(() => {
    let mounted = true;

    const verifyAccess = async () => {
      if (!accessToken) {
        history.replace("/login");
        return;
      }

      try {
        const response = await axios.get(
          `${API_URL}/accounting/account/access/`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );

        if (mounted && response.data.authorized) {
          setAuthorized(true);
        }
      } catch (requestError) {
        if (mounted) {
          setAuthorized(false);
          history.replace("/");
        }
      }
    };

    verifyAccess();

    return () => {
      mounted = false;
    };
  }, [accessToken, history]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  const fetchData = useCallback(
    async ({ silent = false } = {}) => {
      if (!authorized || !accessToken) {
        return;
      }

      const requestId = latestRequest.current + 1;
      latestRequest.current = requestId;

      if (!silent) {
        setLoading(true);
      }

      setError("");

      try {
        if (activeSection === "overview") {
          const response = await axios.get(
            `${API_URL}/accounting/account/overview/`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
              params: {
                ...(dateFrom ? { date_from: dateFrom } : {}),
                ...(dateTo ? { date_to: dateTo } : {}),
              },
            },
          );

          if (requestId !== latestRequest.current) {
            return;
          }

          setOverview(response.data);
          setSectionSummary(null);
          return;
        }

        const params = {
          page: page + 1,
          page_size: pageSize,
        };

        if (ACCOUNT_REPORT_SECTIONS.has(activeSection)) {
          params.scope = "all";
        }

        if (search) {
          params.search = search;
        }

        if (
          selectedDealer
          && STATEMENT_SECTIONS.has(activeSection)
        ) {
          params.dealer_id = selectedDealer.dealer_id;
        }

        if (
          DATE_FILTER_SECTIONS.has(activeSection)
          && dateFrom
        ) {
          params.date_from = dateFrom;
        }

        if (
          DATE_FILTER_SECTIONS.has(activeSection)
          && dateTo
        ) {
          params.date_to = dateTo;
        }

        const catalogUrl = (
          activeSection === "site-orders"
            ? `${API_URL}/inventory/site-orders/`
            : `${API_URL}/accounting/account/catalog/${catalogSection}/`
        );

        const response = await axios.get(
          catalogUrl,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            params,
          },
        );

        if (requestId !== latestRequest.current) {
          return;
        }

        const idField = ROW_ID_FIELDS[catalogSection];

        const preparedRows = response.data.results.map(
          (row, index) => ({
            ...row,
            _account_row_id:
              row[idField]
              ?? row.action_id
              ?? row.actions_id
              ?? `${catalogSection}-${page}-${index}`,
          }),
        );

        setRows(preparedRows);
        setPagination(response.data.pagination);
        setSectionSummary(response.data.summary || null);

        const responsePage = (
          response.data.pagination.page - 1
        );

        if (responsePage !== page) {
          setPage(responsePage);
        }
      } catch (requestError) {
        if (requestId !== latestRequest.current) {
          return;
        }

        if (requestError.response?.status === 403) {
          setAuthorized(false);
          history.replace("/");
          return;
        }

        setError(
          requestError.response?.data?.error
          || "Unable to load Account data.",
        );
      } finally {
        if (
          requestId === latestRequest.current
          && !silent
        ) {
          setLoading(false);
        }
      }
    },
    [
      accessToken,
      activeSection,
      authorized,
      catalogSection,
      dateFrom,
      dateTo,
      history,
      page,
      pageSize,
      search,
      selectedDealer,
    ],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!authorized) {
      return undefined;
    }

    const interval = setInterval(() => {
      if (!document.hidden) {
        fetchData({ silent: true });
      }
    }, REFRESH_INTERVAL);

    const handleFocus = () => {
      fetchData({ silent: true });
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", handleFocus);
    };
  }, [authorized, fetchData]);

  const openDetail = (group, key) => {
    const config = DETAIL_CONFIG[group]?.[key];

    if (!config) {
      return;
    }

    const labels = (
      group === "cards"
        ? CARD_LABELS
        : COUNT_LABELS
    );
    const params = { ...config.params };

    if (NON_CANCELLED_RESELLER_CARD_KEYS.has(key)) {
      if (dateFrom) {
        params.date_from = dateFrom;
      }
      if (dateTo) {
        params.date_to = dateTo;
      }
      params.status_category = "non_cancelled";
    }

    setDetail({
      ...config,
      params,
      title: labels[key] || key,
    });
    setDetailRows([]);
    setDetailPage(0);
    setDetailPageSize(25);
    setDetailError("");
  };


  const applyReportPeriod = () => {
    if (
      draftDateFrom
      && draftDateTo
      && draftDateFrom > draftDateTo
    ) {
      setError(
        "From date cannot be after To date."
      );
      return;
    }

    setDateFrom(draftDateFrom);
    setDateTo(draftDateTo);
    setPage(0);
    setDetail(null);
    setError("");
  };

  const applyReportPreset = (preset) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let start = new Date(today);
    let end = new Date(today);

    if (preset === "all_time") {
      setDraftDateFrom("");
      setDraftDateTo("");
      setDateFrom("");
      setDateTo("");
      setPage(0);
      setDetail(null);
      setError("");
      return;
    }

    if (preset === "yesterday") {
      start.setDate(
        start.getDate() - 1
      );
      end = new Date(start);
    } else if (preset === "this_week") {
      const mondayOffset = (
        start.getDay() + 6
      ) % 7;

      start.setDate(
        start.getDate()
        - mondayOffset
      );
    } else if (preset === "this_month") {
      start = new Date(
        today.getFullYear(),
        today.getMonth(),
        1,
      );
    } else if (preset === "last_month") {
      start = new Date(
        today.getFullYear(),
        today.getMonth() - 1,
        1,
      );

      end = new Date(
        today.getFullYear(),
        today.getMonth(),
        0,
      );
    }

    const nextFrom = toInputDate(start);
    const nextTo = toInputDate(end);

    setDraftDateFrom(nextFrom);
    setDraftDateTo(nextTo);
    setDateFrom(nextFrom);
    setDateTo(nextTo);
    setPage(0);
    setDetail(null);
    setError("");
  };

  const openPeriodDetail = (
    section,
    row,
  ) => {
    setDetail({
      section,
      fields: PERIOD_DETAIL_FIELDS[
        section
      ],
      params: {
        ...(
          section !== "payments"
            ? { scope: "all" }
            : {}
        ),
        status_category: row.key,
        ...(
          dateFrom
            ? { date_from: dateFrom }
            : {}
        ),
        ...(
          dateTo
            ? { date_to: dateTo }
            : {}
        ),
      },
      title: `${row.label} ${section}`,
    });

    setDetailRows([]);
    setDetailPage(0);
    setDetailPageSize(25);
    setDetailError("");
  };


  const closeDetail = () => {
    latestDetailRequest.current += 1;
    setDetail(null);
    setDetailRows([]);
    setDetailError("");
  };

  useEffect(() => {
    if (!authorized || !accessToken || !detail) {
      return undefined;
    }

    let mounted = true;

    const fetchDetail = async (
      { silent = false } = {},
    ) => {
      const requestId = (
        latestDetailRequest.current + 1
      );

      latestDetailRequest.current = requestId;

      if (!silent) {
        setDetailLoading(true);
      }

      setDetailError("");

      try {
        const response = await axios.get(
          `${API_URL}/accounting/account/catalog/${detail.section}/`,
          {
            params: {
              page: detailPage + 1,
              page_size: detailPageSize,
              ...detail.params,
            },
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );

        if (
          !mounted
          || requestId !== latestDetailRequest.current
        ) {
          return;
        }

        const idField = ROW_ID_FIELDS[detail.section];

        const preparedRows = (
          response.data.results || []
        ).map((row, index) => ({
          ...row,
          _account_detail_row_id:
            row[idField]
            ?? `${detail.section}-${detailPage}-${index}`,
        }));

        setDetailRows(preparedRows);
        setDetailPagination(
          response.data.pagination,
        );
      } catch (requestError) {
        if (
          mounted
          && requestId === latestDetailRequest.current
        ) {
          setDetailError("Unable to load records.");
        }
      } finally {
        if (
          mounted
          && requestId === latestDetailRequest.current
        ) {
          setDetailLoading(false);
        }
      }
    };

    fetchDetail();

    const interval = setInterval(() => {
      if (!document.hidden) {
        fetchDetail({ silent: true });
      }
    }, REFRESH_INTERVAL);

    const handleDetailFocus = () => {
      fetchDetail({ silent: true });
    };

    window.addEventListener(
      "focus",
      handleDetailFocus,
    );

    return () => {
      mounted = false;
      latestDetailRequest.current += 1;
      clearInterval(interval);
      window.removeEventListener(
        "focus",
        handleDetailFocus,
      );
    };
  }, [
    accessToken,
    authorized,
    detail,
    detailPage,
    detailPageSize,
  ]);

  const selectSection = (section) => {
    setActiveSection(section);
    setPage(0);
    setRows([]);
    setSectionSummary(null);
    setError("");
  };

  const focusCatalogGrid = () => {
    document.querySelector(".account-grid")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const openTodayDetail = (card) => {
    const today = overview?.today?.date;
    const availableColumns = COLUMN_SETS[card.section] || [];

    if (!today) {
      return;
    }

    setDetail({
      section: card.section,
      title: card.label,
      fields: availableColumns
        .map((column) => column.field)
        .filter((field) => field !== "actions"),
      params: {
        scope: "all",
        date_from: today,
        date_to: today,
        ...(card.statusCategory
          ? { status_category: card.statusCategory }
          : {}),
      },
    });
    setDetailRows([]);
    setDetailPage(0);
    setDetailPageSize(25);
    setDetailError("");
  };

  const openExactStatusDetail = (row) => {
    openPeriodDetail(activeSection, row);

    setDetail((currentDetail) => {
      if (!currentDetail) {
        return currentDetail;
      }

      return {
        ...currentDetail,
        params: {
          ...(currentDetail.params || {}),
          scope: "all",
          ...(search ? { search } : {}),
          ...(dateFrom ? { date_from: dateFrom } : {}),
          ...(dateTo ? { date_to: dateTo } : {}),
          ...(selectedDealer?.dealer_id
            ? { dealer_id: selectedDealer.dealer_id }
            : {}),
        },
      };
    });
  };

  const selectInventorySection = (section) => {
    setActiveInventorySection(section);
    setPage(0);
    setRows([]);
    setError("");
    setSearchInput("");
    setSearch("");
  };

  const fetchStatementSection = async (
    section,
    dealerId,
  ) => {
    const completeRows = [];
    let requestPage = 1;
    let totalPages = 1;

    do {
      const response = await axios.get(
        `${API_URL}/accounting/account/catalog/${section}/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            scope: "reseller",
            dealer_id: dealerId,
            page: requestPage,
            page_size: ACCOUNT_STATEMENT_PAGE_SIZE,
          },
        },
      );

      const payload = response.data || {};

      const pageRows = (
        Array.isArray(payload.results)
          ? payload.results
          : (
            Array.isArray(payload[section])
              ? payload[section]
              : (
                Array.isArray(payload.rows)
                  ? payload.rows
                  : []
              )
          )
      );

      completeRows.push(...pageRows);

      totalPages = Math.max(
        statementNumber(
          payload.pagination?.total_pages,
        ),
        1,
      );

      requestPage += 1;
    } while (requestPage <= totalPages);

    return completeRows;
  };

  const openStatement = async (dealer) => {
    setSelectedDealer(dealer);
    setStatementRows([]);
    setStatementError("");
    setStatementLoading(true);

    try {
      const [
        invoices,
        receipts,
      ] = await Promise.all([
        fetchStatementSection(
          "invoices",
          dealer.dealer_id,
        ),
        fetchStatementSection(
          "receipts",
          dealer.dealer_id,
        ),
      ]);

      setStatementRows(
        buildResellerStatementRows(
          invoices,
          receipts,
        ),
      );
    } catch (requestError) {
      console.error(requestError);

      setStatementError(
        requestError.response?.data?.error
        || "Unable to load the reseller statement.",
      );
    } finally {
      setStatementLoading(false);
    }
  };

  const clearStatement = () => {
    setSelectedDealer(null);
    setStatementRows([]);
    setStatementError("");
    setStatementLoading(false);
  };

  const printStatement = () => {
    if (!selectedDealer || statementLoading) {
      return;
    }

    const printWindow = window.open(
      "",
      "_blank",
      "width=1050,height=800",
    );

    if (!printWindow) {
      setStatementError(
        "Please allow popups to print the statement.",
      );
      return;
    }

    const dealerName = statementDealerName(
      selectedDealer,
    );

    const generatedAt = formatStatementDate(
      new Date(),
    );

    const rowsHtml = statementRows
      .map((row) => `
        <tr>
          <td>${statementEscapeHtml(
            formatStatementDate(row.date),
          )}</td>
          <td>${statementEscapeHtml(row.type)}</td>
          <td>${statementEscapeHtml(row.reference)}</td>
          <td class="description">${statementEscapeHtml(
            row.description,
          )}</td>
          <td>${statementEscapeHtml(row.status)}</td>
          <td class="money">${
            row.debitUsd > 0
              ? formatStatementMoney(row.debitUsd)
              : "-"
          }</td>
          <td class="money">${
            row.creditUsd > 0
              ? formatStatementMoney(row.creditUsd)
              : "-"
          }</td>
          <td class="money">${
            row.creditLbp > 0
              ? formatStatementMoney(row.creditLbp)
              : "-"
          }</td>
        </tr>
      `)
      .join("");

    const printableRows = (
      rowsHtml
      || `
        <tr>
          <td colspan="8" class="empty">
            No invoice or receipt activity was found.
          </td>
        </tr>
      `
    );

    printWindow.document.open();
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Statement of Account - ${
            statementEscapeHtml(dealerName)
          }</title>
          <style>
            @page {
              size: A4 landscape;
              margin: 12mm;
            }

            * {
              box-sizing: border-box;
            }

            body {
              margin: 0;
              color: #111;
              background: #fff;
              font-family: Arial, sans-serif;
              font-size: 11px;
            }

            .statement {
              width: 100%;
            }

            .header {
              display: grid;
              grid-template-columns: 1fr auto 1fr;
              align-items: center;
              gap: 20px;
              padding-bottom: 12px;
              border-bottom: 1px solid #888;
            }

            .company {
              font-size: 13px;
              line-height: 1.35;
            }

            .company strong {
              display: block;
              font-size: 16px;
            }

            .logo {
              width: 82px;
              height: 82px;
              object-fit: contain;
            }

            .generated {
              text-align: right;
              line-height: 1.45;
            }

            h1 {
              margin: 22px 0;
              text-align: center;
              font-size: 24px;
            }

            .party {
              display: flex;
              justify-content: space-between;
              gap: 30px;
              margin-bottom: 18px;
              line-height: 1.5;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
            }

            thead {
              display: table-header-group;
            }

            tr {
              page-break-inside: avoid;
            }

            th,
            td {
              padding: 7px 6px;
              border-bottom: 1px solid #bbb;
              text-align: left;
              vertical-align: top;
              overflow-wrap: anywhere;
            }

            th {
              border-top: 1px solid #777;
              border-bottom: 1px solid #777;
              background: #f0f0f0;
              font-weight: 700;
            }

            th:nth-child(1) {
              width: 13%;
            }

            th:nth-child(2) {
              width: 8%;
            }

            th:nth-child(3) {
              width: 11%;
            }

            th:nth-child(4) {
              width: 27%;
            }

            th:nth-child(5) {
              width: 9%;
            }

            th:nth-child(6),
            th:nth-child(7),
            th:nth-child(8) {
              width: 10.6%;
            }

            .money {
              text-align: right;
              white-space: nowrap;
            }

            .empty {
              padding: 25px;
              text-align: center;
            }

            .totals {
              display: grid;
              grid-template-columns: repeat(5, 1fr);
              gap: 12px;
              margin-top: 20px;
              padding-top: 14px;
              border-top: 1px solid #888;
            }

            .total-label {
              display: block;
              margin-bottom: 5px;
              color: #555;
            }

            .total-value {
              font-size: 13px;
              font-weight: 700;
            }

            .footer {
              display: flex;
              justify-content: space-between;
              margin-top: 28px;
              padding-top: 10px;
              border-top: 1px solid #aaa;
              font-size: 10px;
            }
          </style>
        </head>

        <body>
          <main class="statement">
            <header class="header">
              <div class="company">
                <strong>Omega Speed</strong>
                <span>76 842 442</span>
              </div>

              <img
                id="statement-logo"
                class="logo"
                src="${statementEscapeHtml(statementLogo)}"
                alt="Omega Smart Systems"
              >

              <div class="generated">
                <strong>Generated:</strong><br>
                ${statementEscapeHtml(generatedAt)}
              </div>
            </header>

            <h1>Statement of Account</h1>

            <section class="party">
              <div>
                <strong>Statement To:</strong><br>
                ${statementEscapeHtml(dealerName)}
              </div>

              <div>
                <strong>Period:</strong><br>
                ${statementEscapeHtml(statementPeriod)}
              </div>
            </section>

            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Reference</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th class="money">Invoice USD</th>
                  <th class="money">Receipt USD</th>
                  <th class="money">Receipt LBP</th>
                </tr>
              </thead>

              <tbody>
                ${printableRows}
              </tbody>
            </table>

            <section class="totals">
              <div>
                <span class="total-label">
                  Invoiced USD
                </span>
                <span class="total-value">
                  ${formatStatementMoney(
                    statementTotals.invoicedUsd,
                  )}
                </span>
              </div>

              <div>
                <span class="total-label">
                  Received USD
                </span>
                <span class="total-value">
                  ${formatStatementMoney(
                    statementTotals.receivedUsd,
                  )}
                </span>
              </div>

              <div>
                <span class="total-label">
                  Received LBP
                </span>
                <span class="total-value">
                  ${formatStatementMoney(
                    statementTotals.receivedLbp,
                  )}
                </span>
              </div>

              <div>
                <span class="total-label">
                  Current Balance USD
                </span>
                <span class="total-value">
                  ${formatStatementMoney(
                    selectedDealer.balance_usd,
                  )}
                </span>
              </div>

              <div>
                <span class="total-label">
                  Current Balance LBP
                </span>
                <span class="total-value">
                  ${formatStatementMoney(
                    selectedDealer.balance_lbp,
                  )}
                </span>
              </div>
            </section>

            <footer class="footer">
              <span>Omega Speed</span>
              <span>Statement of Account</span>
            </footer>
          </main>
        </body>
      </html>
    `);
    printWindow.document.close();

    const beginPrint = () => {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 120);
    };

    const logoElement = (
      printWindow.document.getElementById(
        "statement-logo",
      )
    );

    printWindow.onafterprint = () => {
      printWindow.close();
    };

    if (!logoElement || logoElement.complete) {
      beginPrint();
    } else {
      logoElement.onload = beginPrint;
      logoElement.onerror = beginPrint;
    }
  };

  const columns = useMemo(() => {
    const baseColumns = (
      activeSection === "inventory"
        ? (
          INVENTORY_COLUMN_SETS[catalogSection]
          || COLUMN_SETS.inventory
        )
        : COLUMN_SETS[activeSection]
    ) || [];

    if (
      activeSection === "transfer-requests"
    ) {
      return [
        ...baseColumns,
        {
          field: "actions",
          headerName: "Actions",
          minWidth: 130,
          flex: 0,
          sortable: false,
          filterable: false,
          renderCell: ({ row }) => {
            const drawerExchange = (
              String(row.type || "")
                .toLowerCase()
                === "accounting"
            );

            if (!drawerExchange) {
              return "-";
            }

            return (
              <button
                type="button"
                className="account-drawer-report-action"
                onClick={(event) => {
                  event.stopPropagation();
                  setDrawerRequestId(
                    row.request_id
                  );
                }}
              >
                View / Print
              </button>
            );
          },
        },
      ];
    }

    if (activeSection !== "resellers") {
      return baseColumns;
    }

    return [
      ...baseColumns,
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        filterable: false,
        minWidth: 120,
        renderCell: (params) => (
          <button
            type="button"
            className="edit-client"
            onClick={(event) => {
              event.stopPropagation();
              openStatement(params.row);
            }}
          >
            Statement
          </button>
        ),
      },
    ];
  }, [activeSection, catalogSection]);

  const exportCurrentPage = () => {
    const exportRows = rows.map((row) => {
      const cleaned = { ...row };
      delete cleaned._account_row_id;
      return cleaned;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      catalogSection.slice(0, 31),
    );

    XLSX.writeFile(
      workbook,
      `Account-${catalogSection}.xlsx`,
    );
  };

  const AccountToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport />
      <Button
        variant="outline-success"
        onClick={exportCurrentPage}
        style={{
          border: "none",
          background: "transparent",
        }}
      >
        Excel
      </Button>
    </GridToolbarContainer>
  );

  if (authorized === null) {
    return (
      <div className="Plans">
        <div className="main account-loading">
          Checking Account access...
        </div>
      </div>
    );
  }

  if (!authorized) {
    return null;
  }

  return (
    <div className="Plans">
      <div className="main account-page">
        <div className="account-heading">

          <button
            type="button"
            className="edit-client"
            onClick={() => fetchData()}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        <div className="account-tabs">
          {SECTIONS.map((section) => (
            <button
              type="button"
              key={section.key}
              className={
                activeSection === section.key
                  ? "account-tab account-tab-active"
                  : "account-tab"
              }
              onClick={() => selectSection(section.key)}
            >
              {section.label}
            </button>
          ))}
        </div>

        {activeSection === "inventory" && (
          <div
            className="account-inventory-tabs"
            aria-label="Inventory sections"
          >
            {INVENTORY_SECTIONS.map((section) => (
              <button
                type="button"
                key={section.key}
                className={
                  catalogSection === section.key
                    ? (
                      "account-inventory-tab "
                      + "account-inventory-tab-active"
                    )
                    : "account-inventory-tab"
                }
                aria-pressed={
                  catalogSection === section.key
                }
                onClick={() => {
                  selectInventorySection(section.key);
                }}
              >
                {section.label}
              </button>
            ))}
          </div>
        )}

        {selectedDealer
          && STATEMENT_SECTIONS.has(activeSection)
          && (
            <div className="account-statement-bar">
              <span>
                Statement: {selectedDealer.display_name}
              </span>

              <div className="account-statement-actions">
                {[
                  ["transactions", "Transactions"],
                  ["invoices", "Invoices"],
                  ["receipts", "Receipts"],
                ].map(([section, label]) => (
                  <button
                    type="button"
                    key={section}
                    className="edit-client"
                    onClick={() => selectSection(section)}
                  >
                    {label}
                  </button>
                ))}

                <button
                  type="button"
                  className="edit-client"
                  onClick={clearStatement}
                >
                  Clear
                </button>
              </div>
            </div>
          )}

        {error && (
          <div className="account-message">
            {error}
          </div>
        )}

        <Modal
          show={Boolean(selectedDealer)}
          onHide={clearStatement}
          size="xl"
          centered
          className="account-statement-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Statement of Account
            </Modal.Title>
          </Modal.Header>

          <Modal.Body
            style={{
              padding: "24px",
              backgroundColor: "#dfe6eb",
            }}
          >
            {statementError && (
              <div className="account-message">
                {statementError}
              </div>
            )}

            <div
              style={{
                maxWidth: "1080px",
                margin: "0 auto",
                padding: "28px",
                color: "#111",
                backgroundColor: "#fff",
                fontFamily: "Arial, sans-serif",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto 1fr",
                  alignItems: "center",
                  gap: "20px",
                  paddingBottom: "14px",
                  borderBottom: "1px solid #888",
                }}
              >
                <div style={{ lineHeight: 1.4 }}>
                  <strong
                    style={{
                      display: "block",
                      fontSize: "20px",
                    }}
                  >
                    Omega Speed
                  </strong>
                  <span>76 842 442</span>
                </div>

                <img
                  src={statementLogo}
                  alt="Omega Smart Systems"
                  style={{
                    width: "90px",
                    height: "90px",
                    objectFit: "contain",
                  }}
                />

                <div
                  style={{
                    textAlign: "right",
                    lineHeight: 1.45,
                  }}
                >
                  <strong>Generated:</strong>
                  <br />
                  {formatStatementDate(new Date())}
                </div>
              </div>

              <h2
                style={{
                  margin: "25px 0",
                  textAlign: "center",
                }}
              >
                Statement of Account
              </h2>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "30px",
                  marginBottom: "20px",
                }}
              >
                <div>
                  <strong>Statement To:</strong>
                  <br />
                  {statementDealerName(selectedDealer)}
                </div>

                <div>
                  <strong>Period:</strong>
                  <br />
                  {statementPeriod}
                </div>
              </div>

              {statementLoading ? (
                <div
                  style={{
                    padding: "35px",
                    textAlign: "center",
                  }}
                >
                  Loading statement...
                </div>
              ) : (
                <div
                  style={{
                    maxHeight: "52vh",
                    overflow: "auto",
                    borderTop: "1px solid #777",
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "13px",
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          backgroundColor: "#edf0f2",
                        }}
                      >
                        {[
                          "Date",
                          "Type",
                          "Reference",
                          "Description",
                          "Status",
                          "Invoice USD",
                          "Receipt USD",
                          "Receipt LBP",
                        ].map((heading) => (
                          <th
                            key={heading}
                            style={{
                              padding: "9px 7px",
                              borderBottom: "1px solid #777",
                              textAlign: heading.includes("USD")
                                || heading.includes("LBP")
                                ? "right"
                                : "left",
                            }}
                          >
                            {heading}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {statementRows.map((row) => (
                        <tr key={row.key}>
                          <td
                            style={{
                              padding: "8px 7px",
                              borderBottom: "1px solid #ccc",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatStatementDate(row.date)}
                          </td>

                          <td
                            style={{
                              padding: "8px 7px",
                              borderBottom: "1px solid #ccc",
                            }}
                          >
                            {row.type}
                          </td>

                          <td
                            style={{
                              padding: "8px 7px",
                              borderBottom: "1px solid #ccc",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.reference}
                          </td>

                          <td
                            style={{
                              padding: "8px 7px",
                              borderBottom: "1px solid #ccc",
                              minWidth: "230px",
                            }}
                          >
                            {row.description}
                          </td>

                          <td
                            style={{
                              padding: "8px 7px",
                              borderBottom: "1px solid #ccc",
                            }}
                          >
                            {row.status}
                          </td>

                          {[
                            row.debitUsd,
                            row.creditUsd,
                            row.creditLbp,
                          ].map((amount, index) => (
                            <td
                              key={index}
                              style={{
                                padding: "8px 7px",
                                borderBottom: "1px solid #ccc",
                                textAlign: "right",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {amount > 0
                                ? formatStatementMoney(amount)
                                : "-"}
                            </td>
                          ))}
                        </tr>
                      ))}

                      {statementRows.length === 0 && (
                        <tr>
                          <td
                            colSpan={8}
                            style={{
                              padding: "30px",
                              textAlign: "center",
                            }}
                          >
                            No invoice or receipt activity was found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(5, 1fr)",
                  gap: "12px",
                  marginTop: "20px",
                  paddingTop: "15px",
                  borderTop: "1px solid #888",
                }}
              >
                {[
                  [
                    "Invoiced USD",
                    statementTotals.invoicedUsd,
                  ],
                  [
                    "Received USD",
                    statementTotals.receivedUsd,
                  ],
                  [
                    "Received LBP",
                    statementTotals.receivedLbp,
                  ],
                  [
                    "Current Balance USD",
                    selectedDealer?.balance_usd,
                  ],
                  [
                    "Current Balance LBP",
                    selectedDealer?.balance_lbp,
                  ],
                ].map(([label, amount]) => (
                  <div key={label}>
                    <span
                      style={{
                        display: "block",
                        marginBottom: "4px",
                        color: "#555",
                        fontSize: "12px",
                      }}
                    >
                      {label}
                    </span>

                    <strong>
                      {formatStatementMoney(amount)}
                    </strong>
                  </div>
                ))}
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="primary"
              disabled={
                statementLoading
                || Boolean(statementError)
              }
              onClick={printStatement}
            >
              Print Statement
            </Button>

            <Button
              variant="secondary"
              onClick={clearStatement}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={drawerRequestId !== null}
          onHide={() => {
            setDrawerRequestId(null);
          }}
          size="xl"
          className="account-drawer-report-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Drawer Transfer
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {drawerRequestId !== null && (
              <RequestHistory
                requestId={drawerRequestId}
                accountDetail
                onClose={() => {
                  setDrawerRequestId(null);
                }}
              />
            )}
          </Modal.Body>
        </Modal>

        <Modal
          show={Boolean(detail)}
          onHide={closeDetail}
          size="xl"
          centered
          className="account-detail-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {detail?.title}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {detailError && (
              <div className="account-message">
                {detailError}
              </div>
            )}

            <div className="account-detail-grid">
              <DataGrid
                rows={detailRows}
                columns={detailColumns}
                getRowId={(row) => (
                  row._account_detail_row_id
                )}
                getRowHeight={() => "auto"}
                loading={detailLoading}
                rowCount={detailPagination.total_rows}
                page={detailPage}
                pageSize={detailPageSize}
                pagination
                paginationMode="server"
                onPageChange={(newPage) => {
                  setDetailPage(newPage);
                }}
                onPageSizeChange={(newPageSize) => {
                  setDetailPageSize(newPageSize);
                  setDetailPage(0);
                }}
                rowsPerPageOptions={[25, 50, 100]}
                components={{
                  Toolbar: DetailToolbar,
                }}
                disableSelectionOnClick
              />
            </div>
          </Modal.Body>

          <Modal.Footer>
            <button
              type="button"
              className="edit-client"
              onClick={closeDetail}
            >
              Close
            </button>
          </Modal.Footer>
        </Modal>

        {activeSection === "overview" && overview && (
          <>
            <h3 className="account-section-heading">
              Current Reseller Position
            </h3>

            <div className="account-summary-grid">
              {Object.entries(overview.cards || {})
                .filter(([key]) => (
                  CURRENT_POSITION_CARD_KEYS.has(key)
                ))
                .map(([key, value]) => (
                  <button
                    type="button"
                    className="account-summary-card"
                    key={key}
                    onClick={() => openDetail("cards", key)}
                  >
                    <span>{CARD_LABELS[key] || key}</span>
                    <strong>{formatAmount(value)}</strong>
                  </button>
                ))}
            </div>

            <h3 className="account-section-heading">
              Today
            </h3>

            <div className="account-summary-grid">
              {ACCOUNT_TODAY_CARDS.map((card) => (
                <button
                  type="button"
                  className="account-summary-card"
                  key={card.key}
                  onClick={() => openTodayDetail(card)}
                >
                  <span>{card.label}</span>
                  <strong>
                    {card.key.endsWith("_count")
                      || card.key.endsWith("_transfer")
                      ? Number(
                        overview.today?.[card.key] || 0
                      ).toLocaleString()
                      : formatAmount(
                        overview.today?.[card.key] || 0
                      )}
                  </strong>
                </button>
              ))}
            </div>
          </>
        )}

        {activeSection !== "overview" && (
          <>
            {ACCOUNT_REPORT_SECTIONS.has(activeSection) && (
              <section className="account-section-report">
                <section className="account-report-period">
                  <h3>{currentSectionLabel} Period</h3>

                  <div className="account-report-period-controls">
                    <label>
                      From
                      <input
                        type="date"
                        value={draftDateFrom}
                        max={draftDateTo || undefined}
                        onChange={(event) => {
                          setDraftDateFrom(event.target.value);
                        }}
                      />
                    </label>

                    <label>
                      To
                      <input
                        type="date"
                        value={draftDateTo}
                        min={draftDateFrom || undefined}
                        onChange={(event) => {
                          setDraftDateTo(event.target.value);
                        }}
                      />
                    </label>

                    <button
                      type="button"
                      className="edit-client account-report-apply"
                      onClick={applyReportPeriod}
                    >
                      Apply
                    </button>
                  </div>

                  <div className="account-report-presets">
                    {[
                      ["today", "Today"],
                      ["yesterday", "Yesterday"],
                      ["this_week", "This Week"],
                      ["this_month", "This Month"],
                      ["last_month", "Last Month"],
                      ["all_time", "All Time"],
                    ].map(([key, label]) => (
                      <button
                        type="button"
                        className="account-report-preset"
                        key={key}
                        onClick={() => applyReportPreset(key)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </section>
              </section>
            )}

            <div className="account-filters">
              <input
                type="text"
                className="search-client"
                placeholder={`Search ${currentSectionLabel}...`}
                value={searchInput}
                onChange={(event) => {
                  setSearchInput(event.target.value);
                }}
              />
            </div>

            {ACCOUNT_REPORT_SECTIONS.has(activeSection)
              && sectionSummary && (
                <>
                  <h3 className="account-section-heading">
                    Period Totals
                  </h3>

                  <div className="account-summary-grid">
                    {Object.entries(
                      sectionSummary.cards || {}
                    ).map(([key, value]) => (
                      <button
                        type="button"
                        className="account-summary-card"
                        key={key}
                        onClick={focusCatalogGrid}
                      >
                        <span>
                          {ACCOUNT_SECTION_SUMMARY_LABELS[
                            activeSection
                          ]?.[key] || key}
                        </span>
                        <strong>
                          {key === "record_count"
                            ? Number(
                              value || 0
                            ).toLocaleString()
                            : formatAmount(value)}
                        </strong>
                      </button>
                    ))}
                  </div>

                  <h3 className="account-section-heading">
                    Status
                  </h3>

                  <div
                    className={
                      "account-status-cards "
                      + "account-section-status-cards"
                    }
                  >
                    {(sectionSummary.status_rows || []).map(
                      (row) => (
                        <button
                          type="button"
                          className="account-status-card"
                          key={row.key}
                          onClick={() => {
                            openExactStatusDetail(row);
                          }}
                        >
                          <span>{row.label}</span>
                          <strong>
                            {Number(
                              row.count || 0
                            ).toLocaleString()}
                          </strong>
                        </button>
                      )
                    )}
                  </div>

                  <div
                    className={
                      "account-period-grid "
                      + "account-section-status-grid"
                    }
                  >
                    <DataGrid
                      rows={
                        sectionSummary.status_rows || []
                      }
                      columns={
                        activeSection === "invoices"
                          ? INVOICE_STATUS_COLUMNS
                          : activeSection === "receipts"
                            ? RECEIPT_STATUS_COLUMNS
                            : PAYMENT_STATUS_COLUMNS
                      }
                      getRowId={(row) => row.key}
                      autoHeight
                      pageSize={10}
                      rowsPerPageOptions={[10]}
                      components={{
                        Toolbar: DetailToolbar,
                      }}
                      onRowClick={(params) => {
                        openExactStatusDetail(params.row);
                      }}
                      disableSelectionOnClick
                    />
                  </div>

                  <h3 className="account-section-heading">
                    Monthly Activity
                  </h3>

                  <section
                    className={
                      "account-trend-panel "
                      + "account-section-monthly"
                    }
                  >
                    <div className="account-monthly-grid">
                      <DataGrid
                        rows={
                          sectionSummary.monthly_rows || []
                        }
                        columns={
                          activeSection === "invoices"
                            ? MONTHLY_INVOICE_COLUMNS
                            : activeSection === "receipts"
                              ? MONTHLY_RECEIPT_COLUMNS
                              : MONTHLY_PAYMENT_COLUMNS
                        }
                        getRowId={(row) => (
                          `${activeSection}-${row.month}`
                        )}
                        getRowHeight={() => "auto"}
                        autoHeight
                        pageSize={25}
                        rowsPerPageOptions={[25]}
                        pagination
                        components={{
                          Toolbar: MonthlyToolbar,
                        }}
                        disableSelectionOnClick
                      />
                    </div>
                  </section>
                </>
              )}

            <h3
              className={
                "account-section-heading "
                + "account-record-heading"
              }
            >
              {currentSectionLabel} Records
            </h3>

            <div className="account-grid">
              <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(row) => row._account_row_id}
                getRowHeight={() => "auto"}
                loading={loading}
                rowCount={pagination.total_rows}
                page={page}
                pageSize={pageSize}
                pagination
                paginationMode="server"
                onPageChange={(newPage) => {
                  setPage(newPage);
                }}
                onPageSizeChange={(newPageSize) => {
                  setPageSize(newPageSize);
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                components={{
                  Toolbar: AccountToolbar,
                }}
                disableSelectionOnClick
              />
            </div>
          </>
        )}

      </div>
    </div>
  );
}


export default Account;
